/**
 * Bank Account Selector Component
 * Allows organizations to select and manage bank accounts for deposits/payouts
 * Trust Marketplace - Payment Account Management
 */

import React, { useState, useEffect } from 'react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { getWeaveinApiUrl } from '@/src/config';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { authFetch } from '@/lib/authFetch';
import {
  Building2,
  Plus,
  CheckCircle,
  AlertCircle,
  CreditCard,
  Shield,
  Trash2,
  Edit
} from 'lucide-react';

interface BankAccountSelectorProps {
  cboId: number;
  selectedAccountId?: number;
  onSelectAccount: (accountId: number | null) => void;
  purpose?: 'DEPOSIT' | 'PAYOUT';
  label?: string;
}

interface BankAccount {
  id: number;
  financial_institution_id?: number;
  institution_name?: string;
  account_name: string;
  account_number_last4: string;
  routing_number?: string;
  account_type: string;
  is_primary: boolean;
  can_receive_deposits: boolean;
  can_make_payouts: boolean;
  verification_status: string;
  daily_deposit_limit?: number;
  daily_payout_limit?: number;
  is_active: boolean;
  created_at: string;
}

export const BankAccountSelector: React.FC<BankAccountSelectorProps> = ({
  cboId,
  selectedAccountId,
  onSelectAccount,
  purpose = 'DEPOSIT',
  label
}) => {
  const { t } = useAppTranslation();
  const [accounts, setAccounts] = useState<BankAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [adding, setAdding] = useState(false);

  const [newAccount, setNewAccount] = useState({
    account_name: '',
    account_number_encrypted: '', // In production, this should be encrypted client-side
    account_number_last4: '',
    routing_number: '',
    account_type: 'BUSINESS_CHECKING',
    can_receive_deposits: purpose === 'DEPOSIT',
    can_make_payouts: purpose === 'PAYOUT'
  });

  useEffect(() => {
    fetchAccounts();
  }, [cboId]);

  const fetchAccounts = async () => {
    try {
      setLoading(true);
      const response = await authFetch(getWeaveinApiUrl(`/api/organizations/${cboId}/bank-accounts?is_active=true`));

      if (response.ok) {
        const data = await response.json();

        // Filter by purpose if specified
        let filtered = data;
        if (purpose === 'DEPOSIT') {
          filtered = data.filter((acc: BankAccount) => acc.can_receive_deposits);
        } else if (purpose === 'PAYOUT') {
          filtered = data.filter((acc: BankAccount) => acc.can_make_payouts);
        }

        setAccounts(filtered);

        // Auto-select primary account if none selected
        if (!selectedAccountId && filtered.length > 0) {
          const primary = filtered.find((acc: BankAccount) => acc.is_primary);
          if (primary) {
            onSelectAccount(primary.id);
          }
        }
      } else {
        throw new Error('Failed to fetch bank accounts');
      }
    } catch (err) {
      console.error('Error fetching bank accounts:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch accounts');
    } finally {
      setLoading(false);
    }
  };

  const handleAddAccount = async () => {
    try {
      setAdding(true);

      // Validate
      if (!newAccount.account_name || !newAccount.routing_number || !newAccount.account_number_encrypted) {
        alert('Please fill in all required fields');
        return;
      }

      // Extract last 4 digits
      const last4 = newAccount.account_number_encrypted.slice(-4);

      const response = await authFetch(getWeaveinApiUrl(`/api/organizations/${cboId}/bank-accounts`), {
        method: 'POST',
        jsonBody: {
            ...newAccount,
            account_number_last4: last4,
            // TODO: Encrypt account_number_encrypted before sending
            // For now, storing as-is (NOT RECOMMENDED FOR PRODUCTION)
          }
      });

      if (response.ok) {
        const created = await response.json();
        await fetchAccounts();
        setShowAddDialog(false);
        setNewAccount({
          account_name: '',
          account_number_encrypted: '',
          account_number_last4: '',
          routing_number: '',
          account_type: 'BUSINESS_CHECKING',
          can_receive_deposits: purpose === 'DEPOSIT',
          can_make_payouts: purpose === 'PAYOUT'
        });

        // Auto-select the newly added account
        onSelectAccount(created.id);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to add bank account');
      }
    } catch (err) {
      console.error('Error adding bank account:', err);
      alert(err instanceof Error ? err.message : 'Failed to add account');
    } finally {
      setAdding(false);
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <div className="text-red-600 text-sm">
        <AlertCircle className="inline w-4 h-4 mr-1" />
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label>{label || `Bank Account for ${purpose === 'DEPOSIT' ? 'Deposits' : 'Payouts'}`}</Label>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowAddDialog(true)}
          className="flex items-center gap-1"
        >
          <Plus className="w-3 h-3" />
          {t('financial.add_account', 'Add Account')}
        </Button>
      </div>

      {accounts.length === 0 ? (
        <Card className="p-4 text-center">
          <Building2 className="w-8 h-8 text-gray-400 mx-auto mb-2" />
          <p className="text-sm text-gray-600 mb-2">{t('financial.no_bank_accounts_configured', 'No bank accounts configured')}</p>
          <Button onClick={() => setShowAddDialog(true)} size="sm">
            <Plus className="w-4 h-4 mr-1" />
            {t('financial.add_bank_account', 'Add Bank Account')}
          </Button>
        </Card>
      ) : (
        <Select
          value={selectedAccountId?.toString()}
          onValueChange={(value) => onSelectAccount(value ? parseInt(value) : null)}
        >
          <SelectTrigger>
            <SelectValue placeholder={t('financial.select_bank_account', 'Select bank account...')} />
          </SelectTrigger>
          <SelectContent>
            {accounts.map((account) => (
              <SelectItem key={account.id} value={account.id.toString()}>
                <div className="flex items-center gap-2">
                  <CreditCard className="w-4 h-4" />
                  <span>
                    {account.account_name} (****{account.account_number_last4})
                  </span>
                  {account.is_primary && (
                    <Badge variant="outline" className="text-xs">{t('financial.primary', 'Primary')}</Badge>
                  )}
                  {account.verification_status === 'VERIFIED' ? (
                    <CheckCircle className="w-3 h-3 text-green-600" />
                  ) : (
                    <AlertCircle className="w-3 h-3 text-yellow-600" />
                  )}
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      {selectedAccountId && accounts.find(a => a.id === selectedAccountId) && (
        <div className="text-xs text-gray-600 flex items-center gap-2 mt-1">
          {(() => {
            const selected = accounts.find(a => a.id === selectedAccountId);
            return (
              <>
                <Shield className="w-3 h-3" />
                {selected?.verification_status === 'VERIFIED' ? (
                  <span className="text-green-600">{t('financial.verified_account', 'Verified account')}</span>
                ) : (
                  <span className="text-yellow-600">{t('financial.pending_verification', 'Pending verification')}</span>
                )}
                {selected?.institution_name && ` • ${selected.institution_name}`}
              </>
            );
          })()}
        </div>
      )}

      {/* Add Account Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t('financial.add_bank_account_1', 'Add Bank Account')}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label htmlFor="account_name">{t('financial.account_name', 'Account Name *')}</Label>
              <Input
                id="account_name"
                value={newAccount.account_name}
                onChange={(e) => setNewAccount({ ...newAccount, account_name: e.target.value })}
                placeholder="e.g., Main Operating Account"
              />
            </div>

            <div>
              <Label htmlFor="account_type">{t('financial.account_type', 'Account Type')}</Label>
              <Select
                value={newAccount.account_type}
                onValueChange={(value) => setNewAccount({ ...newAccount, account_type: value })}
              >
                <SelectTrigger id="account_type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="BUSINESS_CHECKING">{t('financial.business_checking', 'Business Checking')}</SelectItem>
                  <SelectItem value="CHECKING">{t('financial.personal_checking', 'Personal Checking')}</SelectItem>
                  <SelectItem value="SAVINGS">{t('financial.savings', 'Savings')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="routing_number">{t('financial.routing_number', 'Routing Number *')}</Label>
              <Input
                id="routing_number"
                value={newAccount.routing_number}
                onChange={(e) => setNewAccount({ ...newAccount, routing_number: e.target.value })}
                placeholder="9-digit routing number"
                maxLength={9}
              />
            </div>

            <div>
              <Label htmlFor="account_number">{t('financial.account_number', 'Account Number *')}</Label>
              <Input
                id="account_number"
                type="password"
                value={newAccount.account_number_encrypted}
                onChange={(e) => setNewAccount({ ...newAccount, account_number_encrypted: e.target.value })}
                placeholder={t('financial.account_number_1', 'Account number')}
              />
              <p className="text-xs text-amber-600 mt-1">
                ⚠️ In production, this will be encrypted before transmission
              </p>
            </div>

            <div className="space-y-2">
              <Label>{t('financial.account_purpose', 'Account Purpose')}</Label>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={newAccount.can_receive_deposits}
                    onChange={(e) => setNewAccount({ ...newAccount, can_receive_deposits: e.target.checked })}
                    className="rounded"
                  />
                  <span className="text-sm">{t('financial.receive_deposits', 'Receive Deposits')}</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={newAccount.can_make_payouts}
                    onChange={(e) => setNewAccount({ ...newAccount, can_make_payouts: e.target.checked })}
                    className="rounded"
                  />
                  <span className="text-sm">{t('financial.make_payouts', 'Make Payouts')}</span>
                </label>
              </div>
            </div>

            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setShowAddDialog(false)}>
                {t('financial.cancel', 'Cancel')}
              </Button>
              <Button onClick={handleAddAccount} disabled={adding}>
                {adding ? 'Adding...' : 'Add Account'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BankAccountSelector;
